import { frameIndex, townClock } from '../../lib/clock'
import { el } from '../../lib/dom'
import { withScaffolding, withSmoke, emptyPlot } from './overlays'
import { createPlotRunner, createPlotWorker, type PlotRunner, type RunnerState } from './PlotRunner'
import { createPlotView, type PlotView } from './PlotView'
import type { Env } from '@coderaw/runtime'

export type PlotStage = {
  readonly element: HTMLElement
  readonly runner: PlotRunner
  readonly view: PlotView
  readonly destroy: () => void
}

const STATUS_TEXT: Record<RunnerState, string> = {
  idle: 'waiting for code',
  loading: 'loading',
  running: 'running',
  'syntax-error': 'scaffolding up: the code does not parse yet',
  'runtime-error': 'scaffolding up: the plot threw while drawing',
  overheated: 'overheated: a frame ran past its 2 ms budget',
  crashed: 'the plot froze and was restarted',
}

/** Canvas + runner + the in-world error overlays, wired together. */
/** Space the stage's padding and status line take around the canvas. */
const STAGE_CHROME_PX = 60

export const createPlotStage = (plotId: string, env: () => Env, label: string): PlotStage => {
  const view = createPlotView({ label })
  const status = el('p', { class: 'plot-status', 'aria-live': 'polite' }, STATUS_TEXT.idle)
  const element = el('figure', { class: 'plot-stage' }, view.element, status)
  const runner = createPlotRunner({
    createWorker: createPlotWorker,
    plotId,
    clock: () => {
      const t = townClock()
      return { t, frame: frameIndex(t) }
    },
    env,
  })

  let overlayTimer: number | null = null
  const stopOverlay = (): void => {
    if (overlayTimer !== null) cancelAnimationFrame(overlayTimer)
    overlayTimer = null
  }

  const base = (): Uint8Array => runner.lastFrame() ?? emptyPlot()
  const paint = (state: RunnerState): void => {
    stopOverlay()
    if (state === 'syntax-error' || state === 'runtime-error' || state === 'crashed') {
      view.render(withScaffolding(base()))
    } else if (state === 'overheated') {
      const loop = (): void => {
        view.render(withSmoke(base(), frameIndex(townClock())))
        overlayTimer = requestAnimationFrame(loop)
      }
      loop()
    } else if (state === 'idle') {
      view.render(emptyPlot())
    }
  }

  const unsubState = runner.onState((state, error) => {
    const detail = error && state === 'syntax-error' && error.line ? ` (line ${error.line})` : ''
    status.textContent = `${STATUS_TEXT[state]}${detail}`
    element.dataset['state'] = state
    paint(state)
  })
  const unsubFrame = runner.onFrame((buffer) => view.render(buffer))
  paint('idle')

  // Pick the biggest integer zoom that fits the column and leaves room below.
  const refit = (): void => {
    const column = element.parentElement
    const width = (column?.clientWidth ?? 0) - STAGE_CHROME_PX
    const height = window.innerHeight - 330
    if (width > 0) view.fit(Math.min(width, Math.max(height, 192)))
  }
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(refit)
  queueMicrotask(() => {
    if (element.parentElement) observer?.observe(element.parentElement)
    refit()
  })
  window.addEventListener('resize', refit)

  return {
    element,
    runner,
    view,
    destroy: () => {
      observer?.disconnect()
      window.removeEventListener('resize', refit)
      stopOverlay()
      unsubState()
      unsubFrame()
      runner.dispose()
    },
  }
}
